import React, { useState, useEffect } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { getCompanyInfo, getMyProfile, updateMyProfile } from "../../../api/company";
import { capitalizeFirstLetter, capitalizeWords } from "../../../utils/letters";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyFieldErrors = {
    first_name: "",
    last_name: "",
    email: "",
};

const emptyTouched = {
    first_name: false,
    last_name: false,
    email: false,
};

function validateProfileField(name, value) {
    const val = String(value || "").trim();
    if (name === "first_name") return val ? "" : "El nombre es obligatorio.";
    if (name === "last_name") return val ? "" : "El apellido es obligatorio.";
    if (name === "email") {
        if (!val) return "El correo es obligatorio.";
        return EMAIL_REGEX.test(val) ? "" : "El correo no tiene un formato válido.";
    }
    return "";
}

function mapApiDetailsToFieldErrors(details) {
    if (!details || typeof details !== "object") return null;
    return {
        first_name: Array.isArray(details.first_name) ? String(details.first_name[0] || "") : "",
        last_name: Array.isArray(details.last_name) ? String(details.last_name[0] || "") : "",
        email: Array.isArray(details.email) ? String(details.email[0] || "") : "",
    };
}

function ProfilePortalEmp() {
    const { user } = useAuth(); // Sacamos info básica del contexto
    const [company, setCompany] = useState(null);

    // Estados para el perfil del usuario
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [fieldErrors, setFieldErrors] = useState(emptyFieldErrors);
    const [fieldTouched, setFieldTouched] = useState(emptyTouched);
    const [originalFormData, setOriginalFormData] = useState(null);

    // Estado del formulario
    const [formData, setFormData] = useState({
        username: "",
        first_name: "",
        last_name: "",
        email: "",
    });

    // Cargar datos al montar el componente
    useEffect(() => {
        let isMounted = true;
        async function fetchData() {
            setLoading(true);
            try {
                // 1. Cargamos la organización
                const compData = await getCompanyInfo();
                if (isMounted) setCompany(compData);

                // 2. Cargamos los datos completos del representante
                const userData = await getMyProfile();

                if (isMounted) {
                    // Ahora sí usamos userData en lugar del contexto básico
                    const loadedData = {
                        username: userData.username || "",
                        first_name: userData.first_name || "",
                        last_name: userData.last_name || "",
                        email: userData.email || "",
                    };
                    setFormData(loadedData);
                    setOriginalFormData(loadedData);
                }
            } catch (err) {
                console.error("Error al cargar perfil", err);
                setError("No se pudieron cargar los datos del perfil.");
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        fetchData();
        return () => { isMounted = false; };
    }, [user]);

    // Manejar cambios en los inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleBlur = (field) => {
        setFieldTouched((prev) => ({ ...prev, [field]: true }));
        setFieldErrors((prev) => ({
            ...prev,
            [field]: validateProfileField(field, formData[field]),
        }));
    };

    // Cancelar edición y restaurar datos originales
    const handleCancel = () => {
        setIsEditing(false);
        setError(null);
        setSuccess(null);
        setFieldErrors(emptyFieldErrors);
        setFieldTouched(emptyTouched);
        if (originalFormData) {
            setFormData(originalFormData);
        }
    };

    // Guardar cambios en la base de datos
    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        const nextErrors = {
            first_name: validateProfileField("first_name", formData.first_name),
            last_name: validateProfileField("last_name", formData.last_name),
            email: validateProfileField("email", formData.email),
        };
        setFieldTouched({ first_name: true, last_name: true, email: true });
        setFieldErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) {
            setError(Object.values(nextErrors).find(Boolean));
            return;
        }

        try {
            await updateMyProfile({
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                email: formData.email.trim(),
            });

            setSuccess("¡Datos actualizados correctamente!");
            setOriginalFormData(formData);
            setFieldErrors(emptyFieldErrors);
            setFieldTouched(emptyTouched);
            setIsEditing(false); // Cerramos el modo edición
        } catch (err) {
            const mappedErrors = mapApiDetailsToFieldErrors(err?.response?.data?.details);
            if (mappedErrors) {
                setFieldErrors(mappedErrors);
                setFieldTouched({ first_name: true, last_name: true, email: true });
            }
            setError(err?.response?.data?.error || "Error al actualizar los datos.");
        }
    };

    if (loading) return <div className="p-4 font-n">Cargando perfil...</div>;

    return (
        <main className="p-4 backoffice-tables flex-grow-1 overflow-auto font-n">
            <h2 className="portal-emp-title mb-4">Mi Perfil</h2>

            {error && <div className="alert alert-danger portal-emp-alert">{error}</div>}
            {success && <div className="alert alert-success portal-emp-alert">{success}</div>}

            <div className="row">
                <div className="mb-4">
                    <section className="teacher-profile-card overflow-hidden mb-3">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                <div className="d-flex align-items-center">
                                    <h4 className="mb-0">{capitalizeFirstLetter(company.company_name)}</h4>
                                </div>

                                {/* Botón Editar (Solo se muestra si NO estamos editando) */}
                                {!isEditing ? (
                                    <button
                                        type="button"
                                        className="adm-upd-btn"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <i className="ri-edit-fill me-md-1 d-md-none d-inline"></i>
                                        <span className="d-none d-md-inline">Editar</span>
                                    </button>
                                ) : (
                                    <div className="d-flex gap-2 justify-content-end">
                                        <button
                                            type="button"
                                            className="adm-del-btn"
                                            onClick={handleCancel}
                                        >
                                            <i className="ri-close-circle-line d-inline d-md-none"></i>
                                            <span className="d-md-inline d-none">Cancelar</span>
                                        </button>
                                        <button
                                            type="submit"
                                            form="profileForm"
                                            className="adm-save-btn"
                                        >
                                            <i className="ri-checkbox-circle-line d-inline d-md-none"></i>
                                            <span className="d-md-inline d-none">Guardar Cambios</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!isEditing ? (
                                <div className="mystu-emp-profile d-flex flex-column gap-2 mt-3">
                                    <div className="d-flex align-items-center teacher-profile-course-dates">
                                        <i className="ri-user-line"></i>
                                        <p className="mb-0">Usuario:</p>
                                        <span className="mb-0">{formData.username}</span>
                                    </div>
                                    <div className="d-flex align-items-center teacher-profile-course-dates">
                                        <i className="ri-id-card-line"></i>
                                        <p className="mb-0">Nombres:</p>
                                        <span className="mb-0">{capitalizeWords(formData.first_name) || "—"} {capitalizeWords(formData.last_name) || "—"}</span>
                                    </div>
                                    <div className="d-flex align-items-center teacher-profile-course-dates">
                                        <i className="ri-mail-line"></i>
                                        <p className="mb-0">Email:</p>
                                        <span className="mb-0">{formData.email || "—"}</span>
                                    </div>
                                </div>
                            ) : (
                                <form id="profileForm" onSubmit={handleSave} className="mystu-emp-profile d-flex flex-column gap-3 mt-3">
                                    <div className="row g-3">
                                        <div className="col-12 mb-2">
                                            <label className="mb-0"><span>Usuario:</span></label>
                                            <div className="flex-grow-1">
                                                <input type="text" className="form-control bg-light text-muted" value={formData.username} disabled placeholder="Usuario" />
                                                <div className="d-flex">
                                                    <i className="ri-information-line me-1"></i><span className="small text-muted d-block mt-1">El nombre de usuario no es editable.</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="mb-0"><span>Nombres:</span></label>
                                            <input type="text" name="first_name" className={`form-control flex-grow-1 ${fieldTouched.first_name && fieldErrors.first_name ? "is-invalid" : ""}`} value={capitalizeFirstLetter(formData.first_name)} onChange={handleChange} onBlur={() => handleBlur("first_name")} required placeholder="Ej: Juan" />
                                            {fieldTouched.first_name && fieldErrors.first_name && <div className="invalid-feedback d-block">{fieldErrors.first_name}</div>}
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="mb-0"><span>Apellidos:</span></label>
                                            <input type="text" name="last_name" className={`form-control flex-grow-1 ${fieldTouched.last_name && fieldErrors.last_name ? "is-invalid" : ""}`} value={capitalizeFirstLetter(formData.last_name)} onChange={handleChange} onBlur={() => handleBlur("last_name")} required placeholder="Ej: Perez" />
                                            {fieldTouched.last_name && fieldErrors.last_name && <div className="invalid-feedback d-block">{fieldErrors.last_name}</div>}
                                        </div>
                                        <div className="col-12 mb-2">
                                            <label className="mb-0"><span>Email:</span></label>
                                            <input type="email" name="email" className={`form-control flex-grow-1 ${fieldTouched.email && fieldErrors.email ? "is-invalid" : ""}`} value={formData.email} onChange={handleChange} onBlur={() => handleBlur("email")} required placeholder="Ej: nombre@correo.com" />
                                            {fieldTouched.email && fieldErrors.email && <div className="invalid-feedback d-block">{fieldErrors.email}</div>}
                                        </div>
                                    </div>
                                </form>
                            )}

                        </div>
                    </section>
                </div>

            </div>
        </main>
    );
}

export default ProfilePortalEmp;